import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';

const CROPPER_SIZE_PX = 320;
const OUTPUT_SIZE_PX = 512;
const MAX_ZOOM_MULTIPLIER = 3;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getAllowedMimeType = (mimeType) => {
  const allowed = ['image/jpeg', 'image/png', 'image/webp'];
  return allowed.includes(mimeType) ? mimeType : 'image/png';
};

const buildCroppedFileName = (originalName, mimeType) => {
  const original = (originalName || 'avatar').trim();
  const cleanBase = original.replace(/\.[^/.]+$/, '') || 'avatar';
  const extension = mimeType === 'image/jpeg' ? 'jpg' : mimeType === 'image/webp' ? 'webp' : 'png';
  return `${cleanBase}-cropped.${extension}`;
};

const getOffsetBounds = (image, scale) => {
  if (!image) {
    return { x: 0, y: 0 };
  }
  const halfWidth = (image.width * scale) / 2;
  const halfHeight = (image.height * scale) / 2;
  return {
    x: Math.max(0, halfWidth - CROPPER_SIZE_PX / 2),
    y: Math.max(0, halfHeight - CROPPER_SIZE_PX / 2),
  };
};

const clampOffset = (offset, image, scale) => {
  const bounds = getOffsetBounds(image, scale);
  return {
    x: clamp(offset.x, -bounds.x, bounds.x),
    y: clamp(offset.y, -bounds.y, bounds.y),
  };
};

const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve({
        element: image,
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      });
    };
    image.onerror = () => reject(new Error('Failed to load the selected image.'));
    image.src = src;
  });

const createCroppedAvatar = ({ imageElement, imageSize, scale, offset, fileName, mimeType }) =>
  new Promise((resolve, reject) => {
    const resolvedMimeType = getAllowedMimeType(mimeType);
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE_PX;
    canvas.height = OUTPUT_SIZE_PX;
    const context = canvas.getContext('2d');

    if (!context) {
      reject(new Error('Failed to prepare avatar crop.'));
      return;
    }

    const outputScale = OUTPUT_SIZE_PX / CROPPER_SIZE_PX;
    context.clearRect(0, 0, OUTPUT_SIZE_PX, OUTPUT_SIZE_PX);
    context.setTransform(
      scale * outputScale,
      0,
      0,
      scale * outputScale,
      OUTPUT_SIZE_PX / 2 + offset.x * outputScale,
      OUTPUT_SIZE_PX / 2 + offset.y * outputScale
    );
    context.drawImage(imageElement, -imageSize.width / 2, -imageSize.height / 2);
    context.setTransform(1, 0, 0, 1, 0, 0);

    const quality = resolvedMimeType === 'image/png' ? undefined : 0.92;
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create cropped avatar.'));
          return;
        }

        const croppedFile = new File([blob], buildCroppedFileName(fileName, resolvedMimeType), {
          type: resolvedMimeType,
        });
        const previewUrl = canvas.toDataURL(resolvedMimeType, quality);
        resolve({ croppedFile, previewUrl });
      },
      resolvedMimeType,
      quality
    );
  });

function AvatarCropper({ sourceUrl, sourceFileName, sourceMimeType, onApply, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [imageElement, setImageElement] = useState(null);
  const [imageSize, setImageSize] = useState(null);
  const [minScale, setMinScale] = useState(1);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragState, setDragState] = useState(null);

  const maxScale = useMemo(() => Math.max(minScale, minScale * MAX_ZOOM_MULTIPLIER), [minScale]);

  useEffect(() => {
    let cancelled = false;

    const initializeCropper = async () => {
      try {
        setLoading(true);
        setError('');
        const loaded = await loadImageElement(sourceUrl);
        if (cancelled) {
          return;
        }

        const nextMinScale = Math.max(
          CROPPER_SIZE_PX / loaded.width,
          CROPPER_SIZE_PX / loaded.height
        );

        setImageElement(loaded.element);
        setImageSize({
          width: loaded.width,
          height: loaded.height,
        });
        setMinScale(nextMinScale);
        setScale(nextMinScale);
        setOffset({ x: 0, y: 0 });
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || 'Failed to load image for cropping.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initializeCropper();

    return () => {
      cancelled = true;
    };
  }, [sourceUrl]);

  useEffect(() => {
    if (!imageSize) {
      return;
    }

    setOffset((previous) => clampOffset(previous, imageSize, scale));
  }, [imageSize, scale]);

  const handlePointerDown = (event) => {
    if (!imageSize || loading || processing) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragState({
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffset: offset,
    });
  };

  const handlePointerMove = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId || !imageSize) {
      return;
    }

    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;
    setOffset(
      clampOffset(
        {
          x: dragState.startOffset.x + deltaX,
          y: dragState.startOffset.y + deltaY,
        },
        imageSize,
        scale
      )
    );
  };

  const handlePointerEnd = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }
    setDragState(null);
  };

  const handleZoomChange = (event) => {
    const nextScale = Number(event.target.value);
    if (!Number.isFinite(nextScale)) {
      return;
    }
    setScale(nextScale);
  };

  const handleApply = async () => {
    if (!imageElement || !imageSize) {
      return;
    }

    try {
      setProcessing(true);
      setError('');
      const { croppedFile, previewUrl } = await createCroppedAvatar({
        imageElement,
        imageSize,
        scale,
        offset,
        fileName: sourceFileName,
        mimeType: sourceMimeType,
      });
      onApply(croppedFile, previewUrl);
    } catch (applyError) {
      setError(applyError.message || 'Failed to apply avatar crop.');
    } finally {
      setProcessing(false);
    }
  };

  const imageStyle =
    imageSize && !loading
      ? {
          width: `${imageSize.width * scale}px`,
          height: `${imageSize.height * scale}px`,
          transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
        }
      : undefined;

  return (
    <div className="settings-avatar-cropper">
      <div className="settings-avatar-cropper-header">
        <h3>Crop avatar</h3>
        <p>Move and zoom to choose the square area to save (1:1).</p>
      </div>

      <div
        className={`settings-avatar-cropper-viewport ${dragState ? 'is-dragging' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
      >
        {loading && (
          <div className="settings-avatar-cropper-loading">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {!loading && imageSize && (
          <img
            src={sourceUrl}
            alt="Avatar crop source"
            className="settings-avatar-cropper-image"
            style={imageStyle}
            draggable={false}
          />
        )}
        <div className="settings-avatar-cropper-grid" />
      </div>

      <Form.Group controlId="settings-avatar-zoom" className="settings-avatar-cropper-zoom">
        <Form.Label>Zoom</Form.Label>
        <Form.Range
          min={minScale}
          max={maxScale}
          step={0.01}
          value={scale}
          onChange={handleZoomChange}
          disabled={loading || processing || !imageSize}
        />
      </Form.Group>

      {error && <div className="settings-error-text">{error}</div>}

      <div className="settings-avatar-cropper-actions">
        <Button variant="outline-light" onClick={onCancel} disabled={processing}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleApply} disabled={loading || processing || !imageSize}>
          {processing ? 'Applying...' : 'Apply crop'}
        </Button>
      </div>
    </div>
  );
}

export default AvatarCropper;
